import moment from 'moment'
import * as Sentry from '@sentry/browser'

import { Day, ParsedDate, Professional, Schedule, ScheduleEvent } from './schedule.types'
import { DAYS_WITH_NUMBERS } from './constants'
import { SchedulesCollisionError } from '@errors/schedule/schedule-collision.error'

export const getSchedulesRaw = (data: Professional): ScheduleEvent[] => {
  return data.schedules.flatMap((item) => {
    const firstPeriod = getFirstPeriod(item)
    const periods: ParsedDate[] = [firstPeriod]

    if (hasDoubleShift(item)) {
      const secondPeriod = getSecondPeriod(item)
      periods.push(secondPeriod)
    }

    return periods.map((period) => {
      if ('startSecondPeriod' in period) {
        const { startSecondPeriod, endSecondPeriod } = period
        return { start: startSecondPeriod, end: endSecondPeriod }
      }
      const { startFirstPeriod, endFirstPeriod } = period
      return { start: startFirstPeriod, end: endFirstPeriod }
    })
  })
}

export const hasCollisions = (data: ScheduleEvent[]): boolean => {
  const sorted = data.sort((a, b) => a.start.getTime() - b.start.getTime())
  const collisions = sorted.filter((item, index) => {
    const nextItem = sorted[index + 1]
    if (!nextItem) return false
    return item.end.getTime() > nextItem.start.getTime()
  })
  return collisions.length > 0
}

export const getSchedules = (data: Professional): ScheduleEvent[] => {
  const schedules = getSchedulesRaw(data)
  if (hasCollisions(schedules)) {
    const error = new SchedulesCollisionError()
    Sentry.setExtra('schedules', JSON.stringify(data))
    Sentry.captureException(error)
  }
  return schedules
}

export const hasDoubleShift = (item: Schedule) => {
  // when there is double shift means that both shift have duplicated values
  return item.start_first_period !== item.start_second_period
}

export const getFirstPeriod = (item: Schedule): ParsedDate => {
  return {
    startFirstPeriod: getParseDate({ min: item.start_first_period, weekDay: item.day }),
    endFirstPeriod: getParseDate({ min: item.end_first_period, weekDay: item.day }),
  }
}
export const getSecondPeriod = (item: Schedule) => {
  return {
    startSecondPeriod: getParseDate({ min: item.start_second_period, weekDay: item.day }),
    endSecondPeriod: getParseDate({ min: item.end_second_period, weekDay: item.day }),
  }
}

export const getParseDate = ({ min, weekDay }: { min: number; weekDay: Day }) => {
  const numDay = getNumWeekDay(weekDay)
  return moment().set(getHoursMinutesFromMinutes(min)).isoWeekday(numDay).toDate()
}

export const getHoursMinutesFromMinutes = (min: number) => {
  const hour = Math.floor(min / 60)
  const minute = min - hour * 60
  return { hour: hour === 24 ? 0 : hour, minute }
}

export const getNumWeekDay = (day: Day) => {
  return DAYS_WITH_NUMBERS[day]
}
